/**
 * WHAT BACKEND CAN SAY, as the guest-checkout screens need to hear it.
 *
 * PROVISIONAL, AND DELIBERATELY NOT GRAPHQL. These are the answers the draft
 * `booking-v1@1.11.0` describes for `requestGuestEmailCode`,
 * `verifyGuestEmailCode` and the extended `createBooking` (Backend branch
 * `backend/pilot-launch-architecture`, commit 5ab78f8b, UNPUBLISHED). They are
 * written as plain domain values so the state machine and the screens can be
 * built and tested now, WITHOUT hand-writing a single GraphQL document against
 * an unpublished schema. When 1.11 is published and pinned, one adapter maps
 * the generated types onto these, and a mismatch is a compile error there,
 * not a behaviour change here.
 *
 * Every field is a SERVER value. Durations are never authored on this side:
 * a wait is `resendAfter` or `retryAfterSeconds`, a deadline is a timestamp.
 *
 * The parsers enforce the draft's "present iff" rules. An answer that breaks
 * them is not guessed at — it is `malformed`, and the caller fails closed.
 */

/** An ISO-8601 instant, exactly as Backend sent it. */
export type Instant = string;

export type CodeRequestAnswer =
  /** The provider ACCEPTED the message. Not proof it arrived. */
  | {
      readonly status: "SENT";
      readonly verificationId: string;
      readonly maskedEmail: string;
      readonly expiresAt: Instant;
      readonly resendAfter: Instant;
    }
  | { readonly status: "RESEND_TOO_SOON"; readonly resendAfter: Instant }
  | { readonly status: "RATE_LIMITED"; readonly retryAfterSeconds: number }
  /** No code can be delivered right now; guest checkout is unavailable. */
  | { readonly status: "DELIVERY_UNAVAILABLE" }
  /** Guest verification is switched off. */
  | { readonly status: "UNAVAILABLE" }
  /** This key already produced a booking; only a same-key replay may reach it. */
  | { readonly status: "ATTEMPT_ALREADY_BOOKED" };

export type CodeVerifyAnswer =
  | { readonly status: "VERIFIED" | "ALREADY_VERIFIED"; readonly proofExpiresAt: Instant }
  | { readonly status: "CODE_INCORRECT"; readonly attemptsRemaining: number | null }
  | { readonly status: "CODE_EXPIRED" }
  | { readonly status: "ATTEMPTS_EXHAUSTED" }
  | { readonly status: "CHALLENGE_NOT_FOUND" }
  | { readonly status: "RATE_LIMITED"; readonly retryAfterSeconds: number };

/**
 * One call's result, as the transport saw it. `lost` means NO answer arrived:
 * the request may or may not have been processed. `malformed` means an answer
 * arrived that breaks the contract's own rules; it is treated as a refusal of
 * the step, never as success.
 */
export type Call<T> =
  | { readonly kind: "answered"; readonly answer: T }
  | { readonly kind: "lost" }
  | { readonly kind: "malformed" };

const isInstant = (value: unknown): value is Instant =>
  typeof value === "string" && value.length > 0 && Number.isFinite(Date.parse(value));

const isNonEmpty = (value: unknown): value is string => typeof value === "string" && value.length > 0;

const isWholeSeconds = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

/** `null` means the answer broke the draft's presence rules. */
export function parseCodeRequestAnswer(raw: unknown): CodeRequestAnswer | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  switch (r.status) {
    case "SENT":
      return isNonEmpty(r.verificationId) &&
        isNonEmpty(r.maskedEmail) &&
        isInstant(r.expiresAt) &&
        isInstant(r.resendAfter)
        ? {
            status: "SENT",
            verificationId: r.verificationId,
            maskedEmail: r.maskedEmail,
            expiresAt: r.expiresAt,
            resendAfter: r.resendAfter,
          }
        : null;
    case "RESEND_TOO_SOON":
      return isInstant(r.resendAfter) ? { status: "RESEND_TOO_SOON", resendAfter: r.resendAfter } : null;
    case "RATE_LIMITED":
      return isWholeSeconds(r.retryAfterSeconds)
        ? { status: "RATE_LIMITED", retryAfterSeconds: r.retryAfterSeconds }
        : null;
    case "DELIVERY_UNAVAILABLE":
    case "UNAVAILABLE":
    case "ATTEMPT_ALREADY_BOOKED":
      return { status: r.status };
    default:
      return null;
  }
}

export function parseCodeVerifyAnswer(raw: unknown): CodeVerifyAnswer | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  switch (r.status) {
    case "VERIFIED":
    case "ALREADY_VERIFIED":
      return isInstant(r.proofExpiresAt) ? { status: r.status, proofExpiresAt: r.proofExpiresAt } : null;
    case "CODE_INCORRECT": {
      const left = r.attemptsRemaining;
      if (left === null || left === undefined) return { status: "CODE_INCORRECT", attemptsRemaining: null };
      return typeof left === "number" && Number.isInteger(left) && left >= 0
        ? { status: "CODE_INCORRECT", attemptsRemaining: left }
        : null;
    }
    case "CODE_EXPIRED":
    case "ATTEMPTS_EXHAUSTED":
    case "CHALLENGE_NOT_FOUND":
      return { status: r.status };
    case "RATE_LIMITED":
      return isWholeSeconds(r.retryAfterSeconds)
        ? { status: "RATE_LIMITED", retryAfterSeconds: r.retryAfterSeconds }
        : null;
    default:
      return null;
  }
}

/**
 * The six digits, from whatever the person typed or pasted. Spaces, hyphens
 * and the thin spaces a mail client inserts are removed; anything else that
 * is not a digit makes the value unusable rather than silently stripped, so a
 * pasted sentence does not become a code by accident.
 *
 * Returns null unless exactly six ASCII digits remain.
 */
export function normalizeCode(typed: string): string | null {
  const compact = typed.replace(/[\s   -]/g, "");
  return /^[0-9]{6}$/.test(compact) ? compact : null;
}
